import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, fmtDate, ROLE_LABEL } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/bits";
import { IntegrationManual } from "@/components/IntegrationManual";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, AudioLines, Ban, CheckCircle2, Copy, KeyRound, Loader2, Mail, Phone, Plus, ScanFace, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminTenantDetail() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [keyDialog, setKeyDialog] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [notifEmail, setNotifEmail] = useState("");
  const [notifAdmin, setNotifAdmin] = useState(true);
  const [savingNotif, setSavingNotif] = useState(false);
  const [chaveModo, setChaveModo] = useState("checkin");
  const [savingChave, setSavingChave] = useState(false);
  const [facial2fa, setFacial2fa] = useState(false);
  const [savingFacial, setSavingFacial] = useState(false);
  const [vozComandos, setVozComandos] = useState([]);
  const [savingVoz, setSavingVoz] = useState(false);
  const [gravUrl, setGravUrl] = useState("");
  const [gravTenant, setGravTenant] = useState("");
  const [gravToken, setGravToken] = useState("");
  const [savingGrav, setSavingGrav] = useState(false);
  const [testingGrav, setTestingGrav] = useState(false);
  const [emailCfg, setEmailCfg] = useState(true);
  const notifSeeded = useRef(false);

  const load = () => api.get(`/admin/tenants/${id}`).then(({ data }) => {
    setTenant(data);
    if (!notifSeeded.current) {
      notifSeeded.current = true;
      setNotifEmail(data.notification_email || "");
      setNotifAdmin(data.notify_tenant_admin ?? true);
      setChaveModo(data.chave_virtual_modo || "checkin");
      setFacial2fa(!!data.facial_2fa_enabled);
      setVozComandos((data.voz_comandos || []).map((c) => ({
        texto: c.texto || "", qr: c.qr || c.ponto || "", critico: !!c.critico,
      })));
      setEmailCfg(data.email_configurado !== false);
      setGravUrl(data.grav_url || "");
      setGravTenant(data.grav_tenant || "");
      setGravToken(data.grav_token || "");
    }
  }).catch(() => toast.error("Tenant não encontrado"));
  useEffect(() => { load(); }, [id]);

  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      await api.patch(`/admin/tenants/${id}`, {
        notification_email: notifEmail,
        notify_tenant_admin: notifAdmin,
      });
      toast.success("Configurações de notificação salvas");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingNotif(false); }
  };

  const saveChave = async () => {
    setSavingChave(true);
    try {
      await api.patch(`/admin/tenants/${id}`, { chave_virtual_modo: chaveModo });
      toast.success("Modo da Chave Virtual salvo");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingChave(false); }
  };

  const saveGrav = async () => {
    setSavingGrav(true);
    try {
      await api.patch(`/admin/tenants/${id}`, {
        grav_url: gravUrl.trim(), grav_tenant: gravTenant.trim(), grav_token: gravToken.trim(),
      });
      toast.success("Servidor de gravações salvo");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingGrav(false); }
  };

  const saveFacial = async () => {
    setSavingFacial(true);
    try {
      await api.patch(`/admin/tenants/${id}`, { facial_2fa_enabled: facial2fa });
      toast.success("Configuração de 2FA facial salva");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingFacial(false); }
  };

  const saveVoz = async () => {
    setSavingVoz(true);
    try {
      await api.patch(`/admin/tenants/${id}`, {
        voz_comandos: vozComandos.filter((c) => c.texto.trim() || c.qr.trim()),
      });
      toast.success("Comandos de voz salvos");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingVoz(false); }
  };

  const testarGrav = async () => {
    setTestingGrav(true);
    try {
      const { data } = await api.post(`/admin/tenants/${id}/testar-gravacoes`);
      if (data.ok) {
        toast.success(`Conexão OK — ${data.total} gravação(ões) na pasta do tenant`);
      } else {
        const motivos = {
          incompleto: "Salve URL, pasta e token antes de testar",
          token_recusado: "PHP respondeu, mas recusou o token",
          indisponivel: "Servidor não respondeu (confira URL/pasta e se o listar.php foi instalado)",
        };
        toast.error(motivos[data.motivo] || "Falha no teste de conexão");
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setTestingGrav(false); }
  };

  const copyKey = async (keyId) => {
    try {
      const { data } = await api.get(`/admin/api-keys/${keyId}/reveal`);
      await navigator.clipboard.writeText(data.api_key);
      toast.success("Chave de API copiada");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePlan = async () => {
    try {
      await api.patch(`/admin/tenants/${id}`, { plan_status: tenant.plan_status === "active" ? "suspended" : "active" });
      toast.success("Status da assinatura atualizado");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createKey = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/tenants/${id}/api-keys`, { name: keyName });
      setNewKey(data.api_key);
      setKeyDialog(false);
      setKeyName("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const revoke = async (keyId) => {
    try {
      await api.delete(`/admin/api-keys/${keyId}`);
      toast.success("Chave revogada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!tenant) return <Spinner />;
  const active = tenant.plan_status === "active";

  return (
    <div data-testid="admin-tenant-detail">
      <Link to="/admin/tenants" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 mb-4 transition-colors duration-200">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar para tenants
      </Link>
      <PageHeader title={tenant.name} subtitle={`slug: ${tenant.slug} · criado em ${fmtDate(tenant.created_at)}`}>
        <Badge className={`border-0 text-[11px] hover:bg-transparent ${active ? "text-emerald-400 bg-emerald-400/10" : "text-rose-400 bg-rose-400/10"}`}>
          Assinatura {active ? "Ativa" : "Suspensa"}
        </Badge>
        <Button data-testid="toggle-plan-button" onClick={togglePlan} variant="outline"
          className={`rounded-md border-zinc-700 ${active ? "text-rose-400 hover:bg-rose-400/10" : "text-emerald-400 hover:bg-emerald-400/10"} bg-transparent transition-colors duration-200`}>
          {active ? <><Ban className="h-4 w-4 mr-1.5" /> Suspender</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Reativar</>}
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-6" data-testid="notification-settings-card">
        <h2 className="text-sm font-medium text-zinc-100 mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-indigo-400" /> Notificações por e-mail
        </h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-60">
            <Label className="text-xs text-zinc-400">E-mail de notificação do tenant</Label>
            <Input data-testid="notification-email-edit" type="email" value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)} placeholder="suporte@empresa.com.br"
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
          <div className="flex items-center gap-2.5 pb-2.5">
            <Checkbox id="notify-admin-edit" data-testid="notify-admin-edit-checkbox" checked={notifAdmin}
              onCheckedChange={(v) => setNotifAdmin(!!v)} />
            <Label htmlFor="notify-admin-edit" className="text-xs text-zinc-400 cursor-pointer leading-snug">
              Cópia para o gestor do tenant (tenant_admin)
            </Label>
          </div>
          <Button data-testid="save-notification-settings" onClick={saveNotif} disabled={savingNotif}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {savingNotif ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
        <p className="text-xs text-zinc-400 mt-3">O admin master da plataforma sempre recebe. Novos chamados e mudanças de status disparam e-mails.</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-6" data-testid="chave-virtual-settings-card">
        <h2 className="text-sm font-medium text-zinc-100 mb-3 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-indigo-400" /> Chave Virtual (Convites)
        </h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-60">
            <Label className="text-xs text-zinc-400">Modo de acesso do visitante</Label>
            <select data-testid="chave-virtual-modo-select" value={chaveModo}
              onChange={(e) => setChaveModo(e.target.value)}
              className="mt-1.5 w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none">
              <option value="checkin">CPF obrigatório (modo check-in) — padrão</option>
              <option value="automatico">Abertura automática (sem CPF)</option>
            </select>
          </div>
          <Button data-testid="save-chave-virtual-settings" onClick={saveChave} disabled={savingChave}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {savingChave ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
        {!emailCfg && (
          <p className="text-xs text-amber-400 mb-3" data-testid="chave-virtual-email-off">
            EMERGENT_EMAIL_KEY não configurada no backend — o envio de convite por e-mail está indisponível.
          </p>
        )}
        <p className="text-xs text-zinc-400 mt-3">No modo check-in, o visitante informa o CPF na PWA ao ler o QR do ponto. No modo automático, a leitura do QR já libera o acesso.</p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-6" data-testid="facial-2fa-settings-card">
        <h2 className="text-sm font-medium text-zinc-100 mb-3 flex items-center gap-2">
          <ScanFace className="h-4 w-4 text-indigo-400" /> Reconhecimento Facial (2FA)
        </h2>
        <div className="flex items-center gap-3">
          <Checkbox id="facial-2fa" data-testid="facial-2fa-checkbox" checked={facial2fa}
            onCheckedChange={(v) => setFacial2fa(!!v)} />
          <Label htmlFor="facial-2fa" className="text-sm text-zinc-300 cursor-pointer">
            Exigir verificação facial antes de abrir a câmera do QR Acesso no appmobile
          </Label>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <Button data-testid="save-facial-2fa" onClick={saveFacial} disabled={savingFacial}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {savingFacial ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
          <p className="text-xs text-zinc-400">Vale para todos os moradores. Sem cadastro facial, o appmobile propõe o cadastro na primeira tentativa de usar o QR Acesso.</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-6" data-testid="voz-settings-card">
        <h2 className="text-sm font-medium text-zinc-100 mb-1 flex items-center gap-2">
          <AudioLines className="h-4 w-4 text-indigo-400" /> Comandos de Voz (Acesso Star Trek)
        </h2>
        <p className="text-xs text-zinc-500 mb-4">
          Até 10 comandos. No campo do dispositivo, cole a string inteira do QR Code Brave
          (ex.: {"{\"v\":1,...,\"ponto\":\"...\"}"}) ou só o UUID. O morador fala o texto e o dispositivo é acionado.
        </p>
        <div className="space-y-3">
          {vozComandos.map((c, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`voz-cmd-row-${i}`}>
              <span className="text-xs text-zinc-500 w-5">{i + 1}.</span>
              <Input value={c.texto} placeholder="Texto que o morador fala (ex.: Entrada de Morador)"
                data-testid={`voz-cmd-texto-${i}`}
                onChange={(e) => setVozComandos((l) => l.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md flex-1 min-w-44" />
              <Input value={c.qr} placeholder="String do QR Code Brave ou UUID do dispositivo"
                data-testid={`voz-cmd-qr-${i}`}
                onChange={(e) => setVozComandos((l) => l.map((x, j) => j === i ? { ...x, qr: e.target.value } : x))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md flex-1 min-w-56 font-mono text-xs" />
              <Button variant="outline" size="sm" data-testid={`voz-cmd-remover-${i}`}
                onClick={() => setVozComandos((l) => l.filter((_, j) => j !== i))}
                className="border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 px-2" aria-label="Remover">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4">
          {vozComandos.length < 10 && (
            <Button variant="outline" size="sm" data-testid="voz-cmd-adicionar"
              onClick={() => setVozComandos((l) => [...l, { texto: "", qr: "", critico: false }])}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <Plus className="h-4 w-4 mr-1" /> Adicionar comando
            </Button>
          )}
          <Button data-testid="save-voz" onClick={saveVoz} disabled={savingVoz}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {savingVoz ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar comandos"}
          </Button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 mb-6" data-testid="gravacoes-settings-card">
        <h2 className="text-sm font-medium text-zinc-100 mb-3 flex items-center gap-2">
          <Phone className="h-4 w-4 text-indigo-400" /> Gravações da Portaria (Telefone WhatsApp)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-zinc-400">URL base do servidor de gravações</Label>
            <Input data-testid="grav-url-edit" value={gravUrl} onChange={(e) => setGravUrl(e.target.value)}
              placeholder="https://www.braveaccess.com.br/ura"
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Código do tenant (pasta)</Label>
            <Input data-testid="grav-tenant-edit" value={gravTenant} onChange={(e) => setGravTenant(e.target.value)}
              placeholder="13"
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Token (mesmo do PHP)</Label>
            <Input data-testid="grav-token-edit" type="password" value={gravToken} onChange={(e) => setGravToken(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <Button data-testid="save-gravacoes-settings" onClick={saveGrav} disabled={savingGrav}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            {savingGrav ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
          <Button data-testid="testar-gravacoes-btn" onClick={testarGrav} disabled={testingGrav} variant="outline"
            className="rounded-md border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 transition-colors duration-200">
            {testingGrav ? <Loader2 className="h-4 w-4 animate-spin" /> : "Testar conexão"}
          </Button>
          <p className="text-xs text-zinc-400">Usado pela ponte de listagem/stream de gravações no Telefone Portaria (/recordweb). O token nunca é exposto ao navegador do porteiro.</p>
        </div>
      </div>

      <Tabs defaultValue="integracao">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger data-testid="tab-integracao" value="integracao">Integração & API</TabsTrigger>
          <TabsTrigger data-testid="tab-usuarios" value="usuarios">Usuários ({tenant.users.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="integracao" className="mt-6 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-zinc-100 flex items-center gap-2"><KeyRound className="h-4 w-4 text-indigo-400" /> Chaves de API</h2>
              <Button data-testid="new-api-key-button" onClick={() => setKeyDialog(true)} size="sm"
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
                <Plus className="h-4 w-4 mr-1" /> Nova chave
              </Button>
            </div>
            <div className="flex justify-end mb-2">
              <button data-testid="toggle-revoked-keys" onClick={() => setShowRevoked(!showRevoked)}
                className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors duration-200">
                {showRevoked ? "Ocultar revogadas" : "Mostrar revogadas"}
              </button>
            </div>
            {tenant.api_keys.filter((k) => showRevoked || !k.revoked).length === 0 ? (
              <p className="text-sm text-zinc-400">Nenhuma chave ativa.</p>
            ) : (
              <div className="divide-y divide-zinc-800 border border-zinc-800 rounded-md">
                {tenant.api_keys.filter((k) => showRevoked || !k.revoked).map((k) => (
                  <div key={k.id} className="flex items-center gap-3 px-4 py-3" data-testid={`api-key-row-${k.id}`}>
                    <code className="font-mono text-xs text-zinc-300">{k.key_prefix}…</code>
                    <span className="text-sm text-zinc-400">{k.name}</span>
                    {k.revoked
                      ? <Badge className="border-0 text-[11px] text-rose-400 bg-rose-400/10 hover:bg-transparent">Revogada</Badge>
                      : <Badge className="border-0 text-[11px] text-emerald-400 bg-emerald-400/10 hover:bg-transparent">Ativa</Badge>}
                    <span className="text-xs text-zinc-400 ml-auto">{k.last_used_at ? `último uso ${fmtDate(k.last_used_at)}` : "nunca usada"}</span>
                    {k.recoverable && !k.revoked && (
                      <Button data-testid={`copy-key-${k.id}`} variant="ghost" size="icon" title="Copiar chave de API"
                        onClick={() => copyKey(k.id)}
                        className="h-7 w-7 text-zinc-500 hover:text-indigo-400 hover:bg-zinc-800">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {!k.revoked && (
                      <Button data-testid={`revoke-key-${k.id}`} variant="ghost" size="icon" onClick={() => revoke(k.id)}
                        className="h-7 w-7 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6">
            <h2 className="text-lg font-medium text-zinc-100 mb-4">Manual de Integração</h2>
            <IntegrationManual />
          </div>
        </TabsContent>

        <TabsContent value="usuarios" className="mt-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-zinc-500">Nome</th>
                  <th className="px-4 py-3 text-xs font-medium text-zinc-500">E-mail</th>
                  <th className="px-4 py-3 text-xs font-medium text-zinc-500">Papel</th>
                  <th className="px-4 py-3 text-xs font-medium text-zinc-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {tenant.users.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-800/60">
                    <td className="px-4 py-3 text-zinc-100">{u.name}</td>
                    <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-300">{ROLE_LABEL[u.role]}</td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 text-[11px] hover:bg-transparent ${u.active ? "text-emerald-400 bg-emerald-400/10" : "text-zinc-400 bg-zinc-400/10"}`}>
                        {u.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={keyDialog} onOpenChange={setKeyDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Nova chave de API</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">A chave será exibida apenas uma vez após a criação.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createKey} className="space-y-4" data-testid="api-key-form">
            <div>
              <Label className="text-xs text-zinc-400">Nome/identificação da chave</Label>
              <Input data-testid="api-key-name-input" required value={keyName} onChange={(e) => setKeyName(e.target.value)}
                placeholder="Ex.: Integração Brave Access"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
            </div>
            <Button data-testid="api-key-create-submit" type="submit" disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerar chave"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newKey} onOpenChange={() => setNewKey(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Chave gerada</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Guarde a chave em local seguro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">Copie agora — ela não será exibida novamente.</p>
            <code data-testid="new-api-key-value" className="block bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 font-mono text-xs text-emerald-400 break-all select-all">{newKey}</code>
            <Button data-testid="new-key-close" onClick={() => setNewKey(null)} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-md">Entendi, já copiei</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
